//* Vue
import type { SetupContext } from "vue";
//* 组件库
import type { UiTypes } from "various-ui";
import { $loading } from "various-ui";
//* 公共组件
import type { SearchGeneModal } from "@/components/windows";
//* 组件属性
import type { ModuleProps } from "../index";

export const useComposable = (define: ModuleProps, emits: SetupContext<["submit"]>["emit"]) => {
    //* 插件
    const i18n = useI18n();

    //* 响应式数据
    const refs = {
        error: ref<string>(""),
        candidates: ref<UiTypes.candidate[]>([]),
    };

    //* 表单数据
    const data = reactive({
        name: "",
        message: "",
        species: "",
        gene_id: "",
        mutation: "",
        form_type: "",
        form_msg_id: "",
        transcript_id: "",
    });

    //* 数据节点
    const nodes = {
        modal: ref<InstanceType<typeof SearchGeneModal>>(),
    };

    //* 计算属性
    const computeds = {
        //* 表单状态
        status: computed(() => {
            if (define.disabled) return true;
            else if (data.gene_id && data.name && data.mutation && data.transcript_id) return false;
            else {
                return true;
            }
        }),

        //* 基因选择类型
        selector: computed(() => {
            if (data.form_type == "VPP") return [{ field: "taxonomy_id", value: 9606 }];
            else {
                return undefined;
            }
        }),
    };

    //* 函数列表
    const methods = {
        submit: () => {
            //* 开启加载器
            $loading.open({ message: i18n.t("UnderLoad"), delay: 200 });
            //* 检测突变信息是否合法
            api.frontend("/api/mutations/analyze", {
                data: { id: data.gene_id, keyword: data.mutation, transcript: data.transcript_id },
                hidden: true,
                method: "POST",
                error: (error) => (refs.error.value = error.message),
                finally: () => $loading.close(),
                success: (response) => {
                    if (!response?.site) {
                        refs.error.value = i18n.t("CoordinatesNotFound");
                    } else {
                        //* 清空错误信息
                        refs.error.value = "";
                        //* 响应事件
                        emits("submit", data);
                    }
                },
            });
        },

        change: (response: any) => {
            //* 转录本字典
            refs.candidates.value = [];
            response?.data?.map((value: any) => {
                refs.candidates.value.push({ label: value.value, value: value.value });
            });

            //* 数据更新
            data.name = response.name;
            data.gene_id = response.id;
            data.transcript_id = refs.candidates.value?.[0]?.value || "";
        },

        init: () => {
            //* 转录本列表初始化
            const candidates = define.origin?.transcript_options?.map((value: any) => {
                return { label: value.transcript_id, value: value.transcript_id };
            });

            //* 数据更新
            data.name = define.origin?.gene_name || "";
            data.message = define.origin?.message || "";
            data.species = define.origin?.species || "";
            data.gene_id = define.origin?.gene_id || "";
            data.mutation = define.origin?.mutation || "";
            data.form_type = define.origin?.form_type || "";
            data.form_msg_id = define.origin?.form_msg_id || "";
            data.transcript_id = define.origin?.transcript_id || "";
            refs.candidates.value = candidates || [];
        },
    };

    //* 动态属性
    const binds = reactive({
        name: computed(() => {
            return {
                mode: "click" as "click",
                width: "100%",
                placeholder: i18n.t("NewGene"),
            };
        }),
    });

    return { refs, data, nodes, binds, methods, computeds };
};
